import React from 'react';
import { css } from '@emotion/react';
import { i18n_collect_certificates__v2 } from '@yi/i18n/phrases/i18n_collect_certificates__v2';
import { i18n_collect_yoga_international_certificates } from '@yi/i18n/phrases/i18n_collect_yoga_international_certificates';
import { i18n_deepen_your_practice_with_300_courses_challenges } from '@yi/i18n/phrases/i18n_deepen_your_practice_with_300_courses_challenges';
import { i18n_earn_yoga_alliance_credits } from '@yi/i18n/phrases/i18n_earn_yoga_alliance_credits';
import { i18n_for_yoga_teachers_and_seasoned_practitioners_we_offer_comprehensive_education_to_advance_your } from '@yi/i18n/phrases/i18n_for_yoga_teachers_and_seasoned_practitioners_we_offer_comprehensive_education_to_advance_your';
import { i18n_high_quality_on_demand_yoga_meditation_and_breathwork_classes } from '@yi/i18n/phrases/i18n_high_quality_on_demand_yoga_meditation_and_breathwork_classes';
import { i18n_thousands_of_yoga_practices_to_inspire_and_uplift } from '@yi/i18n/phrases/i18n_thousands_of_yoga_practices_to_inspire_and_uplift';
import { i18n_topics_include_ayurveda_longevity_anatomy_physiology_mantras_and_mudras_spirituality_yin } from '@yi/i18n/phrases/i18n_topics_include_ayurveda_longevity_anatomy_physiology_mantras_and_mudras_spirituality_yin';

import { getResponsiveImageAttributes } from '~/src/common/image';
import { Bold } from '~/src/components/i18n/Bold';
import { useLocale } from '~/src/components/LocaleProvider';
import { typography } from '~/src/styles/design-system';
import { resetStyles } from '~/src/styles/reset-styles-mixins';
import { breakpoints } from '~/src/styles/styled-mixins';

import { BreathworkCarousel } from './BreathworkCarousel';
import { CoursesCarousel } from './CoursesCarousel';
import { ReactComponent as Mandala } from './mandala.svg';
import { MeditaionCarousel } from './MeditaionCarousel';
import { YogaCarousel } from './YogaCarousel';
import { commonStyles } from '../common-styles';
import { StartFreeTrialButton } from '../StartFreeTrialButton';

const POSE_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/e9741c0c-b146-4198-af44-a16517314d61?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';
const YACEP_IMG_SRC =
  'https://brooklyn.gaia.com/v1/assets-render/8ba4692d-4a7a-456f-ab2c-ce5e36b21e03?hash=c5309a93e9a10f0e6fac0e3003892d672eca39d63d823a5121f2d39cbdb665f4';

const courseH2Styles = css(resetStyles.heading, commonStyles.contentPadding, typography.bodyText, {
  color: 'var(--Grey-Text, #585858)',
});

const styles = {
  poseImg: css({
    position: 'relative',
    width: '100%',
    marginRight: 'auto',
    zIndex: '10',
    gridRow: '14/16',
    gridColumn: '1/2',
    maxWidth: '80%',
    margin: '0 auto',
    placeSelf: 'end',
    [breakpoints.minTablet]: {
      maxWidth: '60%',
    },
    [breakpoints.minDesktop]: {
      gridColumnStart: '3',
    },
  }),
  mandala: css({
    zIndex: '5',
    opacity: 0.5,
    gridArea: 'mandala',
    width: '70%',
    maxWidth: '430px',
    placeSelf: 'center',
  }),
  container: css({
    display: 'grid',
    overflowX: 'hidden',
    '--Block-Padding': '24px',
    gridTemplateAreas: `
        '.'
        'heading'
        '.'
        'details'
        '.'
        'yogaCarousel'
        '.'
        'meditaionCarousel'
        '.'
        'breathworkCarousel'
        '.'
        'startFreeTrialButton'
        '.'
        'mandala'
        'mandala'
        '.'
        'coursesHeading'
        '.'
        'coursesP1'
        '.'
        'coursesP2'
        '.'
        'yacep'
        '.'
        'coursesCarousel'
        '.'
        'startFreeTrialButtonBottom'
        '.'
      `,
    gridTemplateRows: `
        var(--Section-Padding)
        auto
        var(--Heading-Padding)
        auto
        var(--SubHeading-Padding)
        auto
        var(--Content-Padding)
        auto
        var(--Content-Padding)
        auto
        var(--Section-Padding)
        auto
        var(--Section-Padding)
        1fr
        1fr
        var(--Section-Padding)
        auto
        var(--Heading-Padding)
        auto
        var(--Block-Padding)
        auto
        var(--Block-Padding)
        auto
        var(--SubHeading-Padding)
        auto
        var(--Section-Padding)
        auto
        var(--Section-Padding)
    `,
    [breakpoints.minTablet]: css({
      '--Block-Padding': '40px',
    }),
    [breakpoints.minDesktop]: css({
      '--Block-Padding': '80px',
      gridTemplateAreas: `
        '. . .'
        '. heading .'
        '. . .'
        '. details .'
        '. . .'
        '. yogaCarousel .'
        '. . .'
        '. meditaionCarousel .'
        '. . .'
        '. breathworkCarousel .'
        '. . .'
        '. startFreeTrialButton .'
        '. . .'
        '. mandala .'
        '. mandala .'
        '. . .'
        '. coursesHeading .'
        '. . .'
        '. coursesP1 .'
        '. . .'
        '. coursesP2 .'
        '. . .'
        '. yacep .'
        '. . .'
        '. coursesCarousel .'
        '. . .'
        '. startFreeTrialButtonBottom .'
        '. . .'
      `,
      gridTemplateColumns: `1fr var(--Max-Desktop-Width) 1fr`,
    }),
  }),
  heading: css(commonStyles.contentPadding, typography.heading1, {
    zIndex: 10,
    gridArea: 'heading',
    color: 'var(--Dark-Text, #27292D)',
  }),
  coursesHeading: css(
    commonStyles.contentPadding,
    typography.heading1,
    commonStyles.containerMaxWidth,
    {
      zIndex: 10,
      gridArea: 'coursesHeading',
      color: 'var(--Dark-Text, #27292D)',
    },
  ),
  details: css(resetStyles.heading, commonStyles.contentPadding, typography.bodyText, {
    zIndex: 10,
    color: 'var(--Grey-Text, #585858)',
    gridArea: 'details',
    margin: 0,
    [breakpoints.minTablet]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
  yogaCarousel: css(commonStyles.contentPadding, commonStyles.carouselContainerStyles, {
    gridArea: 'yogaCarousel',
    zIndex: 15,
  }),
  meditaionCarousel: css(commonStyles.contentPadding, commonStyles.carouselContainerStyles, {
    gridArea: 'meditaionCarousel',
    zIndex: 20,
  }),
  breathworkCarousel: css(commonStyles.contentPadding, commonStyles.carouselContainerStyles, {
    gridArea: 'breathworkCarousel',
    zIndex: 30,
  }),
  coursesCarousel: css(commonStyles.contentPadding, commonStyles.carouselContainerStyles, {
    gridArea: 'coursesCarousel',
    zIndex: 40,
  }),
  startFreeTrialButton: css(commonStyles.contentPadding, {
    gridArea: 'startFreeTrialButton',
  }),
  startFreeTrialButtonBottom: css(commonStyles.contentPadding, {
    gridArea: 'startFreeTrialButtonBottom',
  }),
  img: css({
    gridArea: 'img',
  }),
  background: css({
    zIndex: 1,
    gridArea: 'background',
    borderRadius: 'var(--Border-Radius)',
    background: 'var(--Primary-Background, rgba(255, 243, 226, 0.50))',
    gridRow: '1/15',
    gridColumn: '1/-1',
  }),
  yacep: css(commonStyles.contentPadding, typography.bodyText, {
    zIndex: 10,
    color: 'var(--Grey-Text, #585858)',
    gridArea: 'yacep',
    display: 'grid',
    columnGap: '8px',
    alignItems: 'center',
    height: 'fit-content',
    gridTemplateAreas: `'img .'`,
    gridTemplateColumns: `83px 1fr`,
    [breakpoints.minTablet]: {
      columnGap: '24px',
      gridTemplateColumns: `173px 1fr`,
    },
    [breakpoints.minDesktop]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
    img: {
      width: '100%',
      gridArea: 'img',
    },
    div: {
      display: 'flex',
      rowGap: '16px',
      flexDirection: 'column',
    },
    '.mobile': {
      [breakpoints.minTablet]: {
        display: 'none',
      },
    },
    '.desktop': {
      display: 'none',
      [breakpoints.minTablet]: {
        display: 'inline',
      },
    },
  }),
  coursesP1: css(courseH2Styles, {
    zIndex: 10,
    gridArea: 'coursesP1',
    [breakpoints.minDesktop]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
  coursesP2: css(courseH2Styles, {
    zIndex: 10,
    gridArea: 'coursesP2',
    [breakpoints.minDesktop]: [
      typography.subHeading1,
      {
        color: 'var(--Grey-Text, #585858)',
      },
    ],
  }),
};

export const YogaPractices: React.FC = () => {
  const { locale } = useLocale();

  return (
    <section css={styles.container}>
      <div css={styles.background} />
      <h1 css={styles.heading}>{i18n_thousands_of_yoga_practices_to_inspire_and_uplift(locale)}</h1>
      <h2 css={styles.details}>
        {i18n_high_quality_on_demand_yoga_meditation_and_breathwork_classes(locale)}
      </h2>
      <YogaCarousel css={styles.yogaCarousel} />
      <MeditaionCarousel css={styles.meditaionCarousel} />
      <BreathworkCarousel css={styles.breathworkCarousel} />
      <StartFreeTrialButton css={styles.startFreeTrialButton} />
      <img css={styles.poseImg} {...getResponsiveImageAttributes(POSE_IMG_SRC)} />
      <Mandala css={styles.mandala} />
      <h1 css={styles.coursesHeading}>
        {i18n_deepen_your_practice_with_300_courses_challenges(locale)}
      </h1>
      <h2 css={styles.coursesP1}>
        {i18n_for_yoga_teachers_and_seasoned_practitioners_we_offer_comprehensive_education_to_advance_your(
          locale,
        )}
      </h2>
      <h2 css={styles.coursesP2}>
        {i18n_topics_include_ayurveda_longevity_anatomy_physiology_mantras_and_mudras_spirituality_yin(
          locale,
          { transformFn: Bold },
        )}
      </h2>
      <div css={styles.yacep}>
        <img {...getResponsiveImageAttributes(YACEP_IMG_SRC)} />
        <div>
          <span>{i18n_earn_yoga_alliance_credits(locale)}</span>
          <span className="desktop">{i18n_collect_yoga_international_certificates(locale)}</span>
          <span className="mobile">{i18n_collect_certificates__v2(locale)}</span>
        </div>
      </div>
      <CoursesCarousel css={styles.coursesCarousel} />
      <StartFreeTrialButton css={styles.startFreeTrialButtonBottom} />
    </section>
  );
};
